import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import * as LucideIcons from 'lucide-react';
import SEO from '../components/SEO';
import galleryApi from '../services/galleryApi';

// Smart image URL: converts /images/ to /api/images/ for MongoDB-backed serving
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/images/')) return '/api' + url;
  return url;
};

// Fallback handler for broken images
const handleImgError = (e) => {
  e.target.onerror = null;
  e.target.style.display = 'none';
  const parent = e.target.parentElement;
  if (parent && !parent.querySelector('.img-placeholder')) {
    const placeholder = document.createElement('div');
    placeholder.className = 'img-placeholder absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-100';
    placeholder.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="text-teal-300"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg><span class="text-teal-400 text-xs mt-2 font-medium">Image unavailable</span>';
    parent.appendChild(placeholder);
  }
};

const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [galleryItems, setGalleryItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch gallery items and categories on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [items, cats] = await Promise.all([
          galleryApi.getItems(),
          galleryApi.getCategories()
        ]);
        setGalleryItems(items);
        setCategories(cats);
        setError(null);
      } catch (err) {
        console.error('Error fetching gallery data:', err);
        setError('Failed to load gallery. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredItems = useMemo(() => {
    let items = selectedCategory === 'all'
      ? galleryItems
      : galleryItems.filter(item => item.category === selectedCategory);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.name.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q)
      );
    }

    if (sortBy === 'price-low') {
      items = [...items].sort((a, b) => {
        const pa = parseFloat(a.price.replace(/[^0-9.]/g, ''));
        const pb = parseFloat(b.price.replace(/[^0-9.]/g, ''));
        return pa - pb;
      });
    } else if (sortBy === 'price-high') {
      items = [...items].sort((a, b) => {
        const pa = parseFloat(a.price.replace(/[^0-9.]/g, ''));
        const pb = parseFloat(b.price.replace(/[^0-9.]/g, ''));
        return pb - pa;
      });
    } else if (sortBy === 'name') {
      items = [...items].sort((a, b) => a.name.localeCompare(b.name));
    }

    return items;
  }, [selectedCategory, searchQuery, sortBy, galleryItems]);

  const categoryCount = useMemo(() => {
    const counts = {};
    galleryItems.forEach(item => {
      counts[item.category] = (counts[item.category] || 0) + 1;
    });
    counts['all'] = galleryItems.length;
    return counts;
  }, [galleryItems]);

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon size={18} /> : null;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <LucideIcons.Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading gallery...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <LucideIcons.AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cyan-50">
      <SEO
        title="Gallery"
        description="Browse our collection of over 130 unique antiques including fine jewellery, ceramics, collectibles, sculptures, and art. Each item has its own story."
        path="/gallery"
      />
      {/* Hero */}
      <section className="relative py-20 bg-teal-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 50%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(circle at 75% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)'
          }} />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold mb-4 tracking-tight" data-testid="gallery-title">
            Our Collection
          </h1>
          <p className="text-base sm:text-lg text-teal-200 max-w-2xl mx-auto mb-8">
            Discover {galleryItems.length} carefully curated antiques, each with its own unique story
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative" data-testid="gallery-search">
            <LucideIcons.Search className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-400" size={20} />
            <input
              type="text"
              placeholder="Search by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:bg-white/15 backdrop-blur-sm transition-all"
              data-testid="gallery-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-teal-300 hover:text-white transition-colors"
              >
                <LucideIcons.X size={18} />
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-10">
        {/* Category Pills */}
        <div className="mb-8" data-testid="gallery-filters">
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            {categories.map((category) => (
              <button
                key={category.id}
                data-testid={`filter-${category.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-teal-700 text-white shadow-lg shadow-teal-700/25 scale-105'
                    : 'bg-white text-teal-700 border border-teal-200 hover:border-teal-400 hover:shadow-md'
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {getIcon(category.icon)}
                <span>{category.name}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  selectedCategory === category.id
                    ? 'bg-white/20 text-white'
                    : 'bg-teal-50 text-teal-600'
                }`}>
                  {categoryCount[category.id] || 0}
                </span>
              </button>
            ))}
          </div>

          {/* Sort + Count */}
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <p className="text-sm text-gray-500" data-testid="gallery-count">
              Showing <span className="font-semibold text-teal-700">{filteredItems.length}</span> items
              {searchQuery && <span> for "<span className="font-medium">{searchQuery}</span>"</span>}
            </p>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-teal-200 rounded-lg px-3 py-1.5 text-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
              data-testid="gallery-sort"
            >
              <option value="default">Sort: Default</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Name: A to Z</option>
            </select>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="gallery-grid">
          {filteredItems.map((item, index) => (
            <Card
              key={item.id}
              data-testid={`gallery-item-${item.id}`}
              className="group overflow-hidden border-0 shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer bg-white rounded-xl"
              style={{ animationDelay: `${index * 30}ms` }}
              onClick={() => { setSelectedItem(item); setActiveImageIndex(0); }}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                  src={getImageUrl(item.images?.length > 0 ? item.images[0] : item.image)}
                  alt={item.name}
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700 ease-out"
                  loading="lazy"
                  onError={handleImgError}
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center justify-center gap-2 bg-white/95 backdrop-blur-sm text-teal-800 py-2.5 rounded-lg font-medium text-sm shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <LucideIcons.Eye size={16} />
                      View Details
                    </div>
                  </div>
                </div>
                {/* Price tag */}
                <div className="absolute top-3 left-3">
                  <span className="bg-white/95 backdrop-blur-sm text-teal-800 font-bold text-sm px-3 py-1.5 rounded-full shadow-md">
                    {item.price}
                  </span>
                </div>
                {/* Condition */}
                <div className="absolute top-3 right-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium shadow-sm ${
                    item.condition === 'Excellent' ? 'bg-emerald-500 text-white' :
                    item.condition === 'Good' ? 'bg-amber-500 text-white' :
                    'bg-gray-400 text-white'
                  }`}>
                    {item.condition}
                  </span>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-1.5">
                  {item.year}
                </p>
                <h3 className="font-serif font-bold text-gray-900 leading-snug mb-1 line-clamp-2 group-hover:text-teal-700 transition-colors text-sm">
                  {item.name}
                </h3>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-20" data-testid="gallery-empty">
            <LucideIcons.SearchX size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg font-medium">No items found</p>
            <p className="text-gray-400 text-sm mt-1">Try a different search or category</p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
              className="mt-4 text-teal-600 hover:text-teal-700 text-sm font-medium underline underline-offset-4"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Item Detail Modal */}
      {selectedItem && (() => {
        const allImages = selectedItem.images?.length > 0
          ? selectedItem.images
          : [selectedItem.image];
        const hasMultiple = allImages.length > 1;
        return (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
          data-testid="gallery-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative bg-gray-50">
                {/* Main Image */}
                <img
                  src={getImageUrl(allImages[activeImageIndex])}
                  alt={`${selectedItem.name} - Image ${activeImageIndex + 1}`}
                  className="w-full h-80 md:h-full object-cover"
                  data-testid="modal-main-image"
                  onError={handleImgError}
                />

                {/* Navigation Arrows */}
                {hasMultiple && (
                  <>
                    <button
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      onClick={() => setActiveImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1))}
                      data-testid="modal-prev-image"
                    >
                      <LucideIcons.ChevronLeft size={18} />
                    </button>
                    <button
                      className="absolute right-14 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      onClick={() => setActiveImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1))}
                      data-testid="modal-next-image"
                    >
                      <LucideIcons.ChevronRight size={18} />
                    </button>
                  </>
                )}

                {/* Image Counter Badge */}
                {hasMultiple && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm" data-testid="modal-image-counter">
                    {activeImageIndex + 1} / {allImages.length}
                  </div>
                )}

                {/* Close Button */}
                <button
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                  onClick={() => setSelectedItem(null)}
                  data-testid="modal-close"
                >
                  <LucideIcons.X size={20} />
                </button>

                {/* Thumbnail Strip */}
                {hasMultiple && (
                  <div className="absolute bottom-10 left-0 right-0 flex justify-center gap-1.5 px-4 pb-2" data-testid="modal-thumbnails">
                    {allImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImageIndex(idx)}
                        className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                          idx === activeImageIndex
                            ? 'border-teal-500 scale-110 shadow-lg'
                            : 'border-white/50 opacity-70 hover:opacity-100'
                        }`}
                        data-testid={`modal-thumbnail-${idx}`}
                      >
                        <img src={getImageUrl(img)} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" onError={handleImgError} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-8 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    selectedItem.condition === 'Excellent' ? 'bg-emerald-100 text-emerald-700' :
                    selectedItem.condition === 'Good' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {selectedItem.condition}
                  </span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 font-medium">
                    {selectedItem.year}
                  </span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
                    {categories.find(c => c.id === selectedItem.category)?.name}
                  </span>
                </div>

                <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4 leading-tight" data-testid="modal-title">
                  {selectedItem.name}
                </h2>

                <p className="text-gray-600 leading-relaxed mb-6 flex-grow text-sm">
                  {selectedItem.description}
                </p>

                <div className="border-t border-gray-100 pt-6 mt-auto">
                  <div className="flex items-end justify-between mb-6">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Price</p>
                      <span className="text-3xl font-bold text-teal-700">{selectedItem.price}</span>
                    </div>
                  </div>
                  <Button
                    className="w-full bg-teal-700 hover:bg-teal-600 text-white py-6 rounded-xl text-base font-medium shadow-lg shadow-teal-700/20 transition-all hover:shadow-xl hover:shadow-teal-700/30"
                    data-testid="modal-inquire-btn"
                    onClick={() => {
                      setSelectedItem(null);
                      window.location.href = '/contact';
                    }}
                  >
                    <LucideIcons.MessageCircle size={18} className="mr-2" />
                    Inquire About This Item
                  </Button>
                  <p className="text-center text-xs text-gray-400 mt-3">
                    We'll respond within 24 hours
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default GalleryPage;
